import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Restaurants from '../assets/Restaurants';
import './RestaurantList.css';
import { useMetaTags } from '../hooks/useMetaTags';

// Default image to use if no image is provided
const defaultImage = 'https://via.placeholder.com/400x300?text=Restaurant+Image';

function RestaurantList() {
  useMetaTags({
    title: '2 for 1 Restaurant Deals in Dubai | DXB Dates',
    description: 'Discover the best 2 for 1 restaurant deals and dining offers in Dubai. Perfect for date night in the UAE.',
    ogTitle: 'DXB Dates - Best Dining Deals',
    ogImage: 'https://dxbdates.com/og-image.jpg'
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCuisine, setSelectedCuisine] = useState('');
  const [selectedPriceRange, setSelectedPriceRange] = useState('');
  const [selectedArea, setSelectedArea] = useState('');

  // Get unique values for filters
  const cuisines = [...new Set(Restaurants.map(r => r.cuisine))].sort();
  const priceRanges = [...new Set(Restaurants.map(r => r.price?.priceRange).filter(Boolean))].sort();
  const areas = [...new Set(Restaurants.map(r => r.location.area))].sort();

  // Filter restaurants based on search and filters
  const filteredRestaurants = useMemo(() => {
    return Restaurants.filter(restaurant => {
      const matchesSearch = restaurant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           restaurant.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           restaurant.cuisine.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           restaurant.location.area.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCuisine = !selectedCuisine || restaurant.cuisine === selectedCuisine;
      const matchesPriceRange = !selectedPriceRange || restaurant.price?.priceRange === selectedPriceRange;
      const matchesArea = !selectedArea || restaurant.location.area === selectedArea;

      return matchesSearch && matchesCuisine && matchesPriceRange && matchesArea;
    });
  }, [searchTerm, selectedCuisine, selectedPriceRange, selectedArea]);

  const getRestaurantUrl = (index) => {
    const restaurant = filteredRestaurants[index];
    const originalIndex = Restaurants.findIndex(r =>
      r.name === restaurant.name &&
      r.location.address === restaurant.location.address
    );
    return `/restaurant/${originalIndex !== -1 ? originalIndex : 0}`;
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCuisine('');
    setSelectedPriceRange('');
    setSelectedArea('');
  };

  return (
    <div className="restaurant-list-container">
      <div className="restaurant-list-header">
        <h1>All Restaurants</h1>
        <p>Discover amazing dining experiences in Dubai</p>
      </div>

      {/* Search and Filters */}
      <div className="search-filters-section">
        <div className="search-bar">
          <div className="search-input-container">
            <svg className="search-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
            </svg>
            <input
              type="text"
              placeholder="Search restaurants, cuisine, or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        <div className="filters-container">
          <div className="filter-group">
            <select
              value={selectedCuisine}
              onChange={(e) => setSelectedCuisine(e.target.value)}
              className="filter-select"
            >
              <option value="">All Cuisines</option>
              {cuisines.map(cuisine => (
                <option key={cuisine} value={cuisine}>{cuisine}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <select
              value={selectedPriceRange}
              onChange={(e) => setSelectedPriceRange(e.target.value)}
              className="filter-select"
            >
              <option value="">All Price Ranges</option>
              {priceRanges.map(range => (
                <option key={range} value={range}>{range}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <select
              value={selectedArea}
              onChange={(e) => setSelectedArea(e.target.value)}
              className="filter-select"
            >
              <option value="">All Areas</option>
              {areas.map(area => (
                <option key={area} value={area}>{area}</option>
              ))}
            </select>
          </div>

          <button onClick={clearFilters} className="clear-filters-btn">
            Clear Filters
          </button>
        </div>
      </div>

      {/* Results Count */}
      <div className="results-info">
        <p>Showing {filteredRestaurants.length} of {Restaurants.length} restaurants</p>
      </div>

      {/* Restaurant Grid */}
      <div className="restaurant-grid">
        {filteredRestaurants.length > 0 ? (
          filteredRestaurants.map((restaurant, index) => (
            <Link
              key={index}
              to={getRestaurantUrl(index)}
              className="restaurant-card"
            >
              <div className="restaurant-card-image">
                <img
                  src={restaurant.images[0] || defaultImage}
                  alt={restaurant.name}
                  onError={(e) => {
                    e.target.src = defaultImage;
                  }}
                />
                <div className="restaurant-rating">
                  <span>★ {restaurant.rating.$numberDouble}</span>
                </div>
              </div>
              
              <div className="restaurant-card-content">
                <h3 className="restaurant-name">{restaurant.name}</h3>
                <p className="restaurant-cuisine">{restaurant.cuisine}</p>
                <p className="restaurant-description">{restaurant.description}</p>
                
                <div className="restaurant-details">
                  <div className="restaurant-location">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
                    </svg>
                    <span>{restaurant.location.area}</span>
                  </div>
                  
                  <div className="restaurant-price">
                    <span className="price-range">{restaurant.price?.priceRange}</span>
                    <span className="price-for-two">{restaurant.price?.priceForTwo}</span>
                  </div>
                </div>

                <div className="restaurant-features">
                  {restaurant.features.slice(0, 3).map((feature, idx) => (
                    <span key={idx} className="feature-tag">{feature}</span>
                  ))}
                  {restaurant.features.length > 3 && (
                    <span className="feature-tag more">+{restaurant.features.length - 3} more</span>
                  )}
                </div>
              </div>
            </Link>
          ))
        ) : (
          <div className="no-results">
            <h3>No restaurants found</h3>
            <p>Try adjusting your search criteria or filters</p>
            <button onClick={clearFilters} className="btn btn-primary">
              Clear All Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default RestaurantList;
